<script lang="ts" setup>
    import CodeEdit from '@/shared/codeedit/CodeEdit.vue';
    import { encodeIdBase64 } from '@/shared/helpers/utils';
    import TabLayout from './TabLayout.vue';
    import BoolElement from '@/shared/components/BoolElement.vue';
    import { CodeEditContextRefCode, fetchCodeEditContext } from '@/shared/helpers/api.ts';
    import { PipingClassGenericView } from '@/apps/STID_Pipe/pipingClass.ts';
    import { computed, ref, watch } from 'vue';
    import { usePipeClassStore } from '@/stores/pipeClassStore.ts';
    import { requireCanEditCode } from '@/shared/directives/requirePermission';
    import { http } from '@/shared/httpWrapper';

    const vRequireCanEditCode = requireCanEditCode;

    const formKey = ref<number>(0);

    const referableCodes = ref<Record<string, CodeEditContextRefCode[]> | null>(null);
    const pipeClassStore = usePipeClassStore();

    const isFormActive = ref<boolean>(false);

    watch(() => pipeClassStore.currentPipingClassCodeSetName, async (newCsName, oldValue) => {
        if (newCsName && newCsName !== oldValue) {
            await loadReferableCodes(newCsName);
            isFormActive.value = false;
            formKey.value++;
        }
    });

    async function loadReferableCodes(pipingClassCodeSetName: string) {
        const res = (await fetchCodeEditContext(http, { codeSetName: pipingClassCodeSetName })).data;
        referableCodes.value = res.referableCodes;
    }

    const isEditing = computed(() => {
        return isFormActive.value && !!pipeClassStore.selectedPipingClass;
    });

    const codeEditTitle = computed(() => {
        const verb = isEditing.value ? 'Edit' : 'New';
        return verb + ' Piping Class';
    });

    const isCreating = computed(() => {
        return isFormActive.value && !pipeClassStore.selectedPipingClass;
    });

    const existsCodeSetForScope = computed(() => {
        return !pipeClassStore.loading.pipingClassCodeSetName && pipeClassStore.currentPipingClassCodeSetName !== null;
    });

    const pressureClassCodes = computed(() => {
        return referableCodes.value?.PressureClass;
    });

    const materialClasses = computed(() => {
        return referableCodes.value?.MaterialClass;
    });

    const emit = defineEmits<{
        selectPipingClass: [payload: PipingClassGenericView];
        navigateToPipingClassTab: [];
    }>();

    watch(() => pipeClassStore.selectedPipingClass, () => {
        if (pipeClassStore.selectedPipingClass !== null) {
            isFormActive.value = true;
            formKey.value++;
        }
    });

    function onNewPipingClassClicked() {
        isFormActive.value = true;
        pipeClassStore.setPipingClass(null);
        formKey.value++;
    }

    function onCancelClicked() {
        pipeClassStore.setPipingClass(null);
        isFormActive.value = false;
    }

    function onPipingClassNamedClicked() {
        emit('navigateToPipingClassTab');
    }

    async function onSavedPipingClass() {
        const newClasses = await pipeClassStore.loadPipingClasses(true, true);
        if (newClasses && newClasses.length > 0) {
            pipeClassStore.setPipingClass(newClasses[0].Id);
        }
        formKey.value++;
    }

    function onPipeClassSelect(newValue: PipingClassGenericView) {
        pipeClassStore.setPipingClass(newValue.Id);
    }
</script>
<template>
    <tab-layout
        :has-selected-scope="!!pipeClassStore.selectedScope"
        :is-form-active="isFormActive"
        :exists-code-set-for-scope="existsCodeSetForScope"
        :code-edit-title="codeEditTitle">
        <template #toolbar>
            <div
                v-require-can-edit-code="{ libraryName: 'PipingClass', scopes: pipeClassStore.selectedScope ? [pipeClassStore.selectedScope] : [] }"
                class="level has-margin-bottom">
                <div class=" level-left">
                    <b-button
                        class="level-item"
                        type="is-info"
                        :disabled="!pipeClassStore.selectedScope"
                        :model-value="isCreating"
                        @click="onNewPipingClassClicked">
                        <span class="icon is-small">
                            <i
                                class="mdi mdi-plus"
                                aria-hidden="true" />
                        </span>
                        <span>
                            New Piping Class
                        </span>
                    </b-button>
                </div>
            </div>
        </template>
        <template
            #picker>
            <b-table
                :selected="pipeClassStore.selectedPipingClass"
                height="80vh"
                sticky-header
                hoverable
                striped
                bordered
                default-sort="Name"
                :data="pipeClassStore.pipingClasses"
                :loading="pipeClassStore.loading.pipingClasses"
                :mobile-cards="false"
                @select="onPipeClassSelect">
                <b-table-column
                    sortable
                    searchable
                    centered
                    width="6em"
                    field="PressureClass"
                    label="Pressure class">
                    <template
                        #searchable="slotProps">
                        <b-autocomplete
                            v-model="slotProps.filters.PressureClass"
                            placeholder="Filter..."
                            open-on-focus
                            :data="pressureClassCodes"
                            field="identity"
                            clearable>
                            <template #default="{ option }">
                                {{ option.name + " - " + option.description }}
                            </template>
                        </b-autocomplete>
                    </template>
                    <template #default="slotProps">
                        {{ slotProps.row.PressureClass }}
                    </template>
                </b-table-column>
                <b-table-column
                    searchable
                    sortable
                    centered
                    width="6em"
                    field="MaterialClass"
                    label="Material class">
                    <template
                        #searchable="slotProps">
                        <b-autocomplete
                            v-model="slotProps.filters.MaterialClass"
                            placeholder="Filter..."
                            open-on-focus
                            :data="materialClasses"
                            field="identity"
                            clearable>
                            <template #default="{ option }">
                                {{ option.name + " - " + option.description }}
                            </template>
                        </b-autocomplete>
                    </template>
                    <template #default="slotProps">
                        {{ slotProps.row.MaterialClass }}
                    </template>
                </b-table-column>
                <b-table-column
                    searchable
                    sortable
                    width="6em"
                    field="Name"
                    label="Piping class">
                    <template
                        #searchable="slotProps">
                        <b-input
                            v-model="slotProps.filters.Name"
                            placeholder="Filter..." />
                    </template>
                    <template #default="slotProps">
                        <a @click="onPipingClassNamedClicked">
                            {{ slotProps.row.Name }}
                        </a>
                    </template>
                </b-table-column>
                <b-table-column
                    searchable
                    sortable
                    field="Description"
                    label="Description">
                    <template
                        #searchable="slotProps">
                        <b-input
                            v-model="slotProps.filters.Description"
                            placeholder="Filter..."
                            icon="filter" />
                    </template>
                    <template #default="slotProps">
                        {{ slotProps.row.Description }}
                    </template>
                </b-table-column>
                <b-table-column
                    searchable
                    sortable
                    centered
                    field="Corrosion"
                    width="6em"
                    label="Corrosion">
                    <template
                        #searchable="slotProps">
                        <b-input
                            v-model="slotProps.filters.Corrosion"
                            placeholder="Filter..." />
                    </template>
                    <template #default="slotProps">
                        {{ slotProps.row.Corrosion }}
                    </template>
                </b-table-column>
                <b-table-column
                    v-slot="slotProps"
                    label="Reference">
                    {{ slotProps.row.UpdateRef }}
                </b-table-column>
                <b-table-column
                    v-slot="slotProps"
                    sortable
                    centered
                    field="IsValid"
                    label="Valid">
                    <bool-element :value="slotProps.row.IsValid" />
                </b-table-column>
                <template #empty>
                    <div class="has-text-centered">
                        No piping classes
                    </div>
                </template>
            </b-table>
        </template>
        <template #form>
            <code-edit
                :key="formKey"
                library="PipingClass"
                :scopes="pipeClassStore.selectedScope ? [pipeClassStore.selectedScope] : undefined"
                :code-id="pipeClassStore.selectedPipingClass ? encodeIdBase64('Code', pipeClassStore.selectedPipingClass.Id) : undefined"
                :code-set-name="pipeClassStore.currentPipingClassCodeSetName ?? undefined"
                :fields-to-ignore="['attachmentKey']"
                :custom-labels="{ name: 'Piping class'}"
                :use-box-render="false"
                :use-columns="true"
                @cancel="onCancelClicked"
                @refresh="onSavedPipingClass" />
        </template>
    </tab-layout>
</template>
